import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

type ProfileEditModalProps = {
  show: boolean;
  targetRole: string;
  overview: string;
  saving?: boolean;
  onHide: () => void;
  onSave: (payload: { targetRole: string; overview: string }) => Promise<boolean>;
};

export default function ProfileEditModal({
  show,
  targetRole,
  overview,
  saving = false,
  onHide,
  onSave,
}: ProfileEditModalProps) {
  const [role, setRole] = useState('');
  const [summary, setSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setRole(targetRole);
      setSummary(overview);
    }
  }, [show, targetRole, overview]);

  const handleSave = async () => {
    if (!role.trim()) {
      toast.error('Add a target role before saving.');
      return;
    }
    setSubmitting(true);
    try {
      const saved = await onSave({ targetRole: role.trim(), overview: summary.trim() });
      if (saved) {
        onHide();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const busy = saving || submitting;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="ss-app-modal">
      <Modal.Header closeButton>
        <Modal.Title>Edit role & summary</Modal.Title>
      </Modal.Header>
      <Modal.Body className="ssr-modal-body">
        <Form.Group className="mb-3">
          <Form.Label>Target role</Form.Label>
          <Form.Control
            className="ssr-field-input ssr-field-input--title"
            value={role}
            onChange={(event) => setRole(event.target.value)}
            placeholder="e.g. Senior Fullstack Engineer"
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Professional summary</Form.Label>
          <Form.Control
            as="textarea"
            rows={8}
            className="ssr-field-input ssr-field-textarea ssr-field-textarea--lg"
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            placeholder="A short overview of your experience and strengths"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant="success" onClick={() => void handleSave()} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
